import { createAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { GlobalTreatmentTemplateApi, TreatmentTemplateRequestDto, TreatmentTemplateResponseDto } from "../api/client-axios";
import { ApiError } from "../model/apiError";
import { treatmentTemplateFieldKeys } from "../model/localizedEntities";
import { Problem } from "../model/problem";
import { readFileAsync } from "../utils/fileUtils";
import { pruneTranslations } from "../utils/translations";
import { addAsyncCases, apiRejection } from "./apiThunk";
import { apiConfiguration } from "./server";


export interface IImageCache {
    [index: string] : string
}


export interface TreatmentTemplatesState {
    treatmentTemplates?: TreatmentTemplateResponseDto[],
    selectedTreatmentTemplate?: TreatmentTemplateResponseDto,
    imagesCache: IImageCache
    status: "idle" | "loading" | "error" | "adding" | "success" | "auth-error" | "deleting" ;
    imageStatus: "idle" | "loading" | "success" | "failed"
    error?: string | Problem;
    code?: number;
}

const initialTreatmentTemplatesState: TreatmentTemplatesState = {
    status: "idle",
    imageStatus: "idle",
    error: "",
    imagesCache: {} as IImageCache
}

/**
 * Narrows an edited treatment template to what the API accepts: the
 * response-only `id` is dropped and translation rows the admin left blank are
 * pruned, so clearing a translation deletes it server-side.
 *
 * The image is referenced by `imageMediaFileId` and is not localized — one
 * image serves every language.
 *
 * `translations` is omitted entirely when the record carries none, keeping the
 * payload identical to the pre-localization one against backends that do not
 * support translations yet.
 */
export function toTreatmentTemplateRequest(template: TreatmentTemplateResponseDto): TreatmentTemplateRequestDto {
    const { name, instructions, imageMediaFileId, translations } = pruneTranslations(
        template,
        treatmentTemplateFieldKeys
    );
    const request: TreatmentTemplateRequestDto = { name, instructions, imageMediaFileId };
    return translations == null ? request : { ...request, translations };
}

export const fetchTreatmentTemplates = createAsyncThunk<TreatmentTemplateResponseDto[], void, { rejectValue: ApiError }>('treatmentTemplates/fetchTreatmentTemplates', async (_, thunkApi) => {
    try {
        const api = new GlobalTreatmentTemplateApi(apiConfiguration());
        const response = await api.apiV1AdministrationTreatmentTemplateGet();
        return response.data;
    } catch (error: AxiosError | any) {
        return apiRejection(thunkApi, error);
    }
});

export const addTreatmentTemplate = createAsyncThunk<TreatmentTemplateResponseDto[], TreatmentTemplateResponseDto, { rejectValue: ApiError }>('treatmentTemplates/addTreatmentTemplate', async (template, thunkApi) => {
    try {
        const api = new GlobalTreatmentTemplateApi(apiConfiguration());
        await api.apiV1AdministrationTreatmentTemplatePut(toTreatmentTemplateRequest(template));
        const response = await api.apiV1AdministrationTreatmentTemplateGet();
        return response.data;
    } catch (error: AxiosError | any) {
        return apiRejection(thunkApi, error);
    }
});

export const updateTreatmentTemplate = createAsyncThunk<TreatmentTemplateResponseDto[], TreatmentTemplateResponseDto, { rejectValue: ApiError }>('treatmentTemplates/updateTreatmentTemplate', async (template, thunkApi) => {
    try {
        const api = new GlobalTreatmentTemplateApi(apiConfiguration());
        await api.apiV1AdministrationTreatmentTemplateIdPost(template.id!, toTreatmentTemplateRequest(template));
        const response = await api.apiV1AdministrationTreatmentTemplateGet();
        return response.data;
    } catch (error: AxiosError | any) {
        return apiRejection(thunkApi, error);
    }
});

export const deleteTreatmentTemplate = createAsyncThunk<TreatmentTemplateResponseDto[], string, { rejectValue: ApiError }>('treatmentTemplates/deleteTreatmentTemplate', async (id, thunkApi) => {
    try {
        const api = new GlobalTreatmentTemplateApi(apiConfiguration());
        await api.apiV1AdministrationTreatmentTemplateIdDelete(id);
        const response = await api.apiV1AdministrationTreatmentTemplateGet();
        return response.data;
    } catch (error: AxiosError | any) {
        return apiRejection(thunkApi, error);
    }
});

export const getTreatmentTemplateImageContent = createAsyncThunk<string, {id: string, imageId: string}, { rejectValue: ApiError }>('treatmentTemplates/getTreatmentTemplateImageContent', async (args, thunkApi) => {
    try {
        const api = new GlobalTreatmentTemplateApi(apiConfiguration());
        const r = await api.apiV1AdministrationTreatmentTemplateIdImageGet(args.id, { responseType: 'blob' });
        const url = await readFileAsync(r.data);
        return url as string;
    } catch (error: AxiosError | any) {
        return apiRejection(thunkApi, error);
    }
});

export const uploadTreatmentTemplateImageContent = createAsyncThunk<{ imageId: string | undefined, url: string}, File, { rejectValue: ApiError }>('treatmentTemplates/uploadTreatmentTemplateImageContent', async (file, thunkApi) => {
    try {
        const api = new GlobalTreatmentTemplateApi(apiConfiguration());
        const url = await readFileAsync(file);
        const response = await api.apiV1AdministrationTreatmentTemplateImagePost(file)
        return {imageId: response.data.id, url: url as string};
    } catch (error: AxiosError | any) {
        return apiRejection(thunkApi, error);
    }
});


export const selectTreatmentTemplate = createAction("treatmentTemplates/selectTreatmentTemplate", (template?: TreatmentTemplateResponseDto) => ({ payload: template }));
export const clearErrorTreatmentTemplate = createAction("treatmentTemplates/clearErrorTreatmentTemplate");
export const startAddTreatmentTemplate = createAction("treatmentTemplates/startAddTreatmentTemplate", (template?: TreatmentTemplateResponseDto) => ({ payload: template }));
export const startDeleteTreatmentTemplate = createAction("treatmentTemplates/startDeleteTreatmentTemplate");
export const clearImage = createAction("treatmentTemplates/clearImageInCache", ( imageId: string) => ( {payload : imageId}))


const treatmentTemplatesSlice = createSlice({
    name: "treatmentTemplates",
    initialState: initialTreatmentTemplatesState,
    reducers: {},
    extraReducers: builder => {
        addAsyncCases(
            builder,
            [fetchTreatmentTemplates, addTreatmentTemplate, updateTreatmentTemplate, deleteTreatmentTemplate],
            (state, treatmentTemplates) => {
                state.status = "success";
                state.treatmentTemplates = treatmentTemplates;
            }
        );

        builder
            .addCase(selectTreatmentTemplate, (state, action) => {
                state.selectedTreatmentTemplate = action.payload;
            })
            .addCase(clearErrorTreatmentTemplate, (state) => {
                state.status = 'success';
            })
            .addCase(startAddTreatmentTemplate, (state, action) => {
                state.status = 'adding';
                state.selectedTreatmentTemplate = action.payload;
            })
            .addCase(startDeleteTreatmentTemplate, (state) => {
                state.status = 'deleting';
            })
            .addCase(getTreatmentTemplateImageContent.pending, (state) => {
                state.imageStatus = "loading"
            })
            .addCase(getTreatmentTemplateImageContent.rejected, (state) =>{
                state.imageStatus = "failed"
            })
            .addCase(getTreatmentTemplateImageContent.fulfilled, (state,action) =>{
                state.imageStatus = "success";
                state.imagesCache[action.meta.arg.imageId]= action.payload
            })
            .addCase(uploadTreatmentTemplateImageContent.fulfilled, (state, action)=>{
                if(state.selectedTreatmentTemplate != null){
                    state.selectedTreatmentTemplate.imageMediaFileId = action.payload.imageId;
                    state.imagesCache[action.payload.imageId!]= action.payload.url
                }
            })
            .addCase(clearImage,(state,action)=>{
                if(state.selectedTreatmentTemplate?.imageMediaFileId != null){
                    state.imagesCache[action.payload] = "";
                    state.selectedTreatmentTemplate.imageMediaFileId = null;
                }
            });
    }
});

export default treatmentTemplatesSlice.reducer;
